import { Location } from "history";
import React from "react";
import { Link, LinkProps } from "react-router";
import { shouldServePageFromNext } from "../../../../../packages/ui/src";
import { isFlagActive, FeatureFlag } from "../../../shared/helpers/featureFlags";
import { getQueryStringValues, formatQueryString } from "@travellocal/utils/urlHelpers";

interface Props extends LinkProps {
  /**
   * Whether the page should scroll to the top after the link transition completes.
   * Defaults to true.
   */
  scrollOnTransition?: boolean;
}

/**
 * Ensure the to prop is in a consistent state.
 * @param to The value of to prop passed to <Link />
 * @param scrollOnTransition Whether react-router should scroll on link transition. See AppRoot.tsx
 */
const normaliseToLocation = (to: LinkProps["to"], scrollOnTransition: boolean): LinkProps["to"] => {
  let normalised: LinkProps["to"];

  if (typeof to === "string") {
    normalised = { pathname: to, state: { scrollOnTransition } };
  }
  else if (typeof to === "function") {
    normalised = (location: Location) => {
      const descriptor = to(location);
      if (typeof descriptor === "string") {
        return { pathname: descriptor, state: { scrollOnTransition } };
      }
      else {
        const state = descriptor.state || {};
        return { ...descriptor, state: { ...state, scrollOnTransition } };
      }
    };
  }
  else {
    const state = to.state || {};
    normalised = { ...to, state: { ...state, scrollOnTransition } };
  }

  return normalised;
};

/**
 * A wrapper around react-router's Link.
 */
export const ReactRouterLink = ({ to, scrollOnTransition, ...rest }: Props) => {

  // If this is defined, we need to modify to.state
  if (scrollOnTransition != null) {
    to = normaliseToLocation(to, scrollOnTransition);
  }

  // react-router doesn't support absolute URLs - it'll throw an error.
  if (typeof to === "string"
    && (!to.startsWith("/")
      || (isFlagActive(FeatureFlag.NEXTJS_3358) && shouldServePageFromNext(to)))) {
    return <a {...rest} href={to} />
  }

  return (
    <Link {...rest} to={to} />
  );
};

(ReactRouterLink as React.FC).displayName = "ReactRouterLink";
